import { FC, useRef, useState } from 'react';
import { Box, Button, Chip, IconButton, Menu, MenuItem } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { CodeOff, FilterListOff } from '@mui/icons-material';
import { EasTooltip } from 'ui/EasTooltip';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { nanoid } from '@reduxjs/toolkit';
import { useLocation, useNavigate } from 'react-router';
import queryString from 'query-string';
import { useSelector } from 'react-redux';
import { CountryWithFlag } from 'ui/CountryWithFlag';
import { UserAvatar } from 'ui/UserAvatar';
import { getUsersList } from 'redux/reducers/user/selectors';
import { getErrorTypes } from 'redux/reducers/errorCorrections/selectors';
import { COLORS } from 'services/constants/colors';
import { Search } from './Search';
import { TFilter } from '../types';
import { CountrySelect } from '../ErrorsHeader/ActionSpeedDial/FilterField/CountrySelectAutocomplete';
import { TFilterPanelProps } from './types';

export const FilterPanel: FC<TFilterPanelProps> = ({ tableStructure, children }) => {
	const navigate = useNavigate();
	const location = useLocation();
	const { search } = useLocation();
	const navigateParams = queryString.parse(search);
	// const tableStructure = errorsTableStructure() as TTableStructure[];
	const usersList = useSelector(getUsersList);
	const typeOfErrors = useSelector(getErrorTypes);

	const filtersArray: TFilter[] = tableStructure.reduce(
		(acc, item) => {
			if (navigateParams[item.selector])
				acc.push({ name: item.selector, value: navigateParams[item.selector] as string });
			return acc;
		},
		[] as { name: string; value: string }[],
	);

	const [addFilterAnchor, setAddFilterAnchor] = useState<HTMLButtonElement | null>(null);
	const refNewFilter = useRef<HTMLButtonElement>(null);

	const [newFilterIndex, setNewFilterIndex] = useState<number | null>(null);
	// const [filtersArray, setFiltersArray] = useState<{ name: string; value: string }[]>([]);
	const [open, setOpen] = useState(false);
	const [openFilterList, setOpenFilterList] = useState(false);
	const handleClick = () => {
		setOpen(true);
	};
	const handleClose = () => {
		setOpen(false);
	};
	const handleCloseFilterList = () => {
		setOpenFilterList(false);
		setNewFilterIndex(null);
	};
	const handleNewFilter = (index: number) => {
		setNewFilterIndex(index);
		handleClose();
		setOpenFilterList(true);
	};

	const filtersNavigate = (newFiltersArray: TFilter[]) => {
		const searchTmp = newFiltersArray.reduce(
			(acc, item) => {
				if (item?.name && item?.value !== null) {
					acc[String(item.name)] = item.value;
				}
				return acc;
			},
			{} as { [key: string]: string | number | string[] | number[] },
		);
		if (navigateParams?.sortIndex && navigateParams?.sortDirection) {
			searchTmp.sortIndex = navigateParams.sortIndex as string;
			searchTmp.sortDirection = navigateParams.sortDirection as string;
		}
		if (navigateParams?.search) {
			searchTmp.search = navigateParams.search as string;
		}
		navigate({
			pathname: location.pathname,
			search: queryString.stringify(searchTmp),
		});
	};

	const handleSelectScheme = (index: number) => {
		if (newFilterIndex === null) return;
		handleCloseFilterList();
		const newFiltersArray = [...filtersArray];
		const newFilterArrayForDispatch = [...filtersArray];
		let filterIndex = newFiltersArray?.findIndex?.(
			(item) => item?.name === tableStructure[newFilterIndex].selector,
		);
		if (filterIndex > -1 && !!newFiltersArray && !!newFiltersArray?.[filterIndex]?.value) {
			// eslint-disable-next-line @typescript-eslint/ban-ts-comment
			// @ts-ignore: Unreachable code error
			newFiltersArray[filterIndex].value = tableStructure[newFilterIndex].filter?.[index][0] ?? '';
			newFilterArrayForDispatch.splice(filterIndex, 1);
		} else {
			filterIndex = newFiltersArray.length;
			newFiltersArray.push({
				name: tableStructure[newFilterIndex].selector,
				value: tableStructure[newFilterIndex].filter?.[index][0] ?? '',
			});
		}

		newFilterArrayForDispatch.push({
			name: 'schemes',
			value: `${String(tableStructure[newFilterIndex].filter?.[index][0] ?? '')}`,
			comparator: 'like',
		});
		if (newFiltersArray) {
			setNewFilterIndex(null);
			filtersNavigate(newFiltersArray);
		}
	};

	const handleSelectFilter = (index: number) => {
		if (newFilterIndex === null) return;
		if (tableStructure[newFilterIndex].selector === 'schemes') {
			handleSelectScheme(index);
			return;
		}
		handleCloseFilterList();
		const newFiltersArray = [...filtersArray];
		const filterIndex = newFiltersArray?.findIndex?.(
			(item) => item?.name === tableStructure[newFilterIndex].selector,
		);
		if (filterIndex > -1 && !!newFiltersArray && !!newFiltersArray?.[filterIndex]?.value) {
			// eslint-disable-next-line @typescript-eslint/ban-ts-comment
			// @ts-ignore: Unreachable code error
			newFiltersArray[filterIndex].value = tableStructure[newFilterIndex].filter?.[index][0] ?? '';
		} else {
			newFiltersArray.push({
				name: tableStructure[newFilterIndex].selector,
				value: tableStructure[newFilterIndex].filter?.[index][0] ?? '',
			});
		}
		if (newFiltersArray) {
			setNewFilterIndex(null);
			filtersNavigate(newFiltersArray);
		}
	};

	const handleSelectCountry = (country: string) => {
		if (newFilterIndex === null) return;
		handleCloseFilterList();
		const newFiltersArray = [...filtersArray];
		const filterIndex = newFiltersArray?.findIndex?.(
			(item) => item?.name === tableStructure[newFilterIndex].selector,
		);
		if (filterIndex > -1 && !!newFiltersArray && !!newFiltersArray?.[filterIndex]?.value) {
			// eslint-disable-next-line @typescript-eslint/ban-ts-comment
			// @ts-ignore: Unreachable code error
			newFiltersArray[filterIndex].value = country;
		} else {
			newFiltersArray.push({
				name: tableStructure[newFilterIndex].selector,
				value: country,
			});
		}
		if (newFiltersArray) {
			setNewFilterIndex(null);
			filtersNavigate(newFiltersArray);
		}
	};

	const handleRemoveFilter = (index: number) => {
		const newFiltersArray = [...filtersArray];
		newFiltersArray.splice(index, 1);
		filtersNavigate(newFiltersArray);
	};

	const isSorted = !!navigateParams?.sortIndex && !!navigateParams?.sortDirection;
	const hasFilters = filtersArray.some((item) => !!item) || !!navigateParams?.search;

	// Clears the filters and the search string, the sort stays as it is.
	const handleClearAll = () => {
		const searchTmp: Record<string, string> = {};
		if (isSorted) {
			searchTmp.sortIndex = navigateParams.sortIndex as string;
			searchTmp.sortDirection = navigateParams.sortDirection as string;
		}
		navigate({
			pathname: location.pathname,
			search: queryString.stringify(searchTmp),
		});
	};

	const handleClearSort = () => {
		navigate({
			pathname: location.pathname,
			search: queryString.stringify({
				...navigateParams,
				sortIndex: undefined,
				sortDirection: undefined,
			}),
		});
	};

	return (
		<Box
			style={{
				padding: '8px',
				color: COLORS.PRIMARY,
				fontWeight: 'bold',
				display: 'flex',
				flexDirection: 'row',
				justifyContent: 'space-between',
				gap: 2,
			}}
		>
			{children}
			{tableStructure.length > 0 ? (
				<Box sx={{ display: 'flex', gap: 1, minHeight: '28px' }}>
					{isSorted && (
						<EasTooltip title="Clear sort">
							<IconButton onClick={handleClearSort} color="primary" sx={{ p: 0, m: 0 }}>
								<CodeOff
									sx={{ p: 0, m: 0, rotate: '90deg', transform: 'scaleX(-1)' }}
									color="primary"
									fontSize="small"
								/>
							</IconButton>
						</EasTooltip>
					)}

					{hasFilters && (
						<EasTooltip title="Clear all filters">
							<IconButton onClick={handleClearAll} color="primary" sx={{ p: 0, m: 0 }}>
								<FilterListOff sx={{ p: 0, m: 0 }} color="primary" fontSize="small" />
							</IconButton>
						</EasTooltip>
					)}

					{filtersArray?.map?.((item, index) => {
						if (!item) return null;
						if (item.name === 'id' || item.name === 'external_order_id') return null;
						return (
							<Box
								key={nanoid()}
								color="primary"
								style={{
									display: 'inline-flex',
									alignItems: 'center',
									fontSize: '0.9rem',
									fontWeight: 500,
									textTransform: 'none',
									// background: '#00000015',
									borderRadius: '12px',
									padding: '2px 10px',
									gap: 3,
									border: `1px solid ${COLORS.PRIMARY}`,
								}}
							>
								{tableStructure?.find?.((i) => i.selector === item.name)?.name}:{' '}
								<span style={{ fontWeight: 'bolder', marginLeft: '5px' }}>
									{item.name === 'deliveryCountry' ? (
										<CountryWithFlag
											countryCode={String(item.value)}
											size="s"
											sx={{ fontWeight: 'bolder' }}
										/>
									) : item.name === 'typeOfError' ? (
										<Chip
											label={
												typeOfErrors?.find((i) => i.id === +(item?.value ?? 0))?.name ?? 'Unknown'
											}
											size="small"
											// variant="outlined"
											color="error"
											sx={{
												p: '0 5px',
												fontSize: '.8rem',
												height: '20px',
												bgcolor: 'error.light',
												// color: '#00000090',
												fontWeight: '550',
											}}
										/>
									) : item.name === 'responsibleUser' ? (
										<UserAvatar
											user={usersList?.find((i) => i.id === item?.value) ?? null}
											size="xs"
											onClick={() => null}
										/>
									) : (
										(tableStructure
											?.find?.((i) => i.selector === item.name)
											?.filter?.find?.((i) => i[0] === item.value)?.[1] ?? item.value)
									)}
								</span>
								<IconButton style={{ padding: 0 }} onClick={() => handleRemoveFilter(index)}>
									<CloseIcon sx={{ fontSize: '1rem', fontWeight: 'bold' }} color="primary" />
								</IconButton>
							</Box>
						);
					})}
					<Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 1 }}>
						{newFilterIndex !== null && (
							<Button
								ref={refNewFilter}
								color="primary"
								style={{
									fontSize: '0.9rem',
									fontWeight: 500,
									padding: '2px 10px',
									textTransform: 'none',
									borderRadius: '12px',
									border: `1px dashed ${COLORS.PRIMARY}`,
									display: 'flex',
									alignItems: 'center',
									gap: 1,
								}}
							>
								{tableStructure[newFilterIndex].name}{' '}
								<ExpandMoreIcon fontSize="small" sx={{ mt: '3px' }} />
							</Button>
						)}

						<Button
							ref={setAddFilterAnchor}
							color="primary"
							sx={{
								fontWeight: 550,
								fontSize: '0.9rem',
								padding: '2px 10px',
								textTransform: 'none',
								borderRadius: '12px',
								border: `1px dashed ${COLORS.PRIMARY}`,
							}}
							onClick={handleClick}
						>
							Add filter +
						</Button>
						<Menu
							anchorEl={addFilterAnchor}
							open={open}
							onClose={handleClose}
							MenuListProps={{
								'aria-labelledby': 'basic-button',
							}}
							sx={{
								'& .MuiMenu-paper': { borderRadius: '12px' },
							}}
						>
							{tableStructure?.map?.((item, index) => {
								if (!item.filterable) return null;
								return (
									<MenuItem
										key={nanoid()}
										onClick={() => handleNewFilter(index)}
										color="primary"
										sx={{
											color: 'primary.main',
											fontWeight: 'bold',
										}}
									>
										{item.name}
									</MenuItem>
								);
							})}
						</Menu>
						<Menu
							anchorEl={addFilterAnchor}
							open={openFilterList}
							onClose={handleCloseFilterList}
							MenuListProps={{
								'aria-labelledby': 'basic-button',
							}}
							sx={{
								'& .MuiMenu-paper': { borderRadius: '12px', marginLeft: '-90px' },
							}}
						>
							{tableStructure?.[newFilterIndex ?? 0].selector === 'deliveryCountry' ? (
								<CountrySelect onChange={handleSelectCountry} />
							) : tableStructure?.[newFilterIndex ?? 0].selector === 'responsibleUser' ? (
								(usersList?.map?.((user, index) => {
									return (
										<MenuItem
											key={nanoid()}
											onClick={() => handleSelectFilter(index)}
											color="primary"
											sx={{ color: 'primary.main', fontWeight: 'bold' }}
										>
											<UserAvatar user={user} size="sm" onClick={() => null} />
										</MenuItem>
									);
								}) ?? null)
							) : (
								(tableStructure?.[newFilterIndex ?? 0]?.filter?.map?.((item, index) => {
									if (!item) return null;
									return (
										<MenuItem
											key={nanoid()}
											onClick={() => handleSelectFilter(index)}
											color="primary"
											sx={{ color: 'primary.main', fontWeight: 'bold' }}
										>
											{item[1]}
										</MenuItem>
									);
								}) ?? null)
							)}
						</Menu>
					</Box>
				</Box>
			) : (
				<Box />
			)}
			<Search />
		</Box>
	);
};
